import React, { useEffect, useState } from 'react';
import ApiService from '../../../../services/apiService';
import { toast } from 'react-toastify';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getUserId } from '../../../../../Redux/selecters';

const toDatetimeLocal = (isoString) => {
  if (!isoString) return '';
  const d = new Date(isoString);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fromDatetimeLocalToISO = (localValue) => {
  if (!localValue) return null;
  return new Date(localValue).toISOString();
};

const UPDATE_ENDPOINT = 'UPDATE_GIFT_CARD';

const GiftCardDetails = () => {
  const { giftCardCode } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const UserId = useSelector(getUserId);

  const [loading, setLoading] = useState(true);

  // ✅ Keep values in state (so backend still receives them),
  // ✅ but we will NOT render inputs for them.
  const [formData, setFormData] = useState({
    giftCardCode: '',
    itemDescription: '',
    initialAmount: '',
    issuedDateLocal: '',
    isReloadable: false,
    isDigital: false,
    currencyCode: '',
  });

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        const fromState = location?.state?.giftCard;

        if (fromState?.giftCardCode) {
          hydrate(fromState);
        } else {
          const apiService = new ApiService();
          const res = await apiService.get('GET_ALL_GIFT_CARDS');
          const list = res?.data?.result ?? res?.data?.data ?? res?.data ?? [];
          const found = (list || []).find((x) => String(x?.giftCardCode || '').toLowerCase() === String(giftCardCode || '').toLowerCase());

          if (!found) {
            toast.warning('Gift card not found');
            navigate('/admin/itemmaster/GiftCard');
            return;
          }
          hydrate(found);
        }
      } catch (e) {
        console.error(e);
        toast.error('Failed to load gift card');
      } finally {
        setLoading(false);
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [giftCardCode]);

  const hydrate = (gc) => {
    setFormData({
      giftCardCode: gc?.giftCardCode ?? '',
      itemDescription: gc?.itemDescription ?? '',
      initialAmount: gc?.initialAmount ?? '',
      issuedDateLocal: toDatetimeLocal(gc?.issuedDate), // stored but hidden
      isReloadable: Boolean(gc?.isReloadable), // stored but hidden
      isDigital: Boolean(gc?.isDigital), // stored but hidden
      currencyCode: gc?.currencyCode ?? '', // stored but hidden
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ✅ Only validate what user can edit now
  const validate = () => {
    if (!formData.itemDescription?.trim()) return toast.warning('Enter Item Description'), false;
    if (formData.initialAmount === '' || isNaN(Number(formData.initialAmount))) return toast.warning('Enter a valid Initial Amount'), false;
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;

    try {
      const datareq = {
        loggedInUserId: UserId,

        // ✅ backend identifier
        giftCardCode: String(formData.giftCardCode || '').trim(),

        // ✅ editable
        itemDescription: formData.itemDescription.trim(),
        initialAmount: Number(formData.initialAmount),

        // ✅ still sent (hidden in UI)
        issuedDate: fromDatetimeLocalToISO(formData.issuedDateLocal),
        isReloadable: Boolean(formData.isReloadable),
        isDigital: Boolean(formData.isDigital),
        currencyCode: String(formData.currencyCode || '').trim(),
      };

      const apiService = new ApiService();
      const res = await apiService.post(UPDATE_ENDPOINT, datareq);

      if (res?.data?.statusCode === 200 || res?.data?.statusCode === 201) {
        toast.success('Gift Card Updated Successfully');
        navigate('/admin/itemmaster/GiftCard');
      } else {
        toast.warning(res?.data?.message ?? 'Waiting for Response');
      }
    } catch (e) {
      console.error(e);
      toast.error('Update failed');
    }
  };

  if (loading) {
    return (
      <div className="p-lg-5 container">
        <button className="addItem-btn mb-3" onClick={() => navigate(-1)}>
          ← Back
        </button>
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-lg-5 container">
      <button className="addItem-btn mb-3" onClick={() => navigate(-1)}>
        ← Back
      </button>

      <div className="row catlist">
        <h5 className="mb-3">Gift Card Details</h5>

        <div className="form-group row mt-2">
          <div className="col-md-8 mt-2">
            <label className="form-label">Item Description</label>
            <input type="text" className="p-2 form-control" name="itemDescription" value={formData.itemDescription} onChange={handleInputChange} />
          </div>

          <div className="col-md-3 mt-3">
            <label className="form-label">Initial Amount</label>
            <input
              type="number"
              className="p-2 form-control"
              name="initialAmount"
              value={formData.initialAmount}
              onChange={handleInputChange}
              min="0"
              step="0.01"
            />
          </div>

          {/* ✅ HIDDEN FIELDS (do not render):
              - Currency Code
              - Issued Date
              - Reloadable checkbox
              - Digital checkbox
          */}
        </div>

        <div className="row justify-content-end mt-4">
          <div className="col-lg-2 col-md-3 addItem-btn p-3 mt-4 mb-3 mx-3" onClick={handleSave}>
            Save Changes
          </div>
        </div>
      </div>
    </div>
  );
};

export default GiftCardDetails;
